import { useApp, useBehavior, useOnline, useTriggerAction, useUpdateWidgetValue } from '@libs/core';
import { DefaultTheme, TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useResolvedTheme } from '../../theme/useResolvedTheme';
import { TTurnstileData } from '../../types/data';
import { ConsumerActionTypeEnums } from '../../types/enums';
import { TTurnstileOption } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import styles from './turnstile.module.css';
import {
    buildErrorValue,
    buildSuccessValue,
    loadTurnstileScript,
    normalizeAppearance,
    normalizeSize,
    TurnstileClientError,
    TurnstileLoadError,
    TTurnstileRenderOptions,
} from './turnstileUtils';

const getErrorMessage = (error: unknown) => (error instanceof Error ? error.message : String(error));

export const Turnstile = ({ widget }: TWidgetProps<TTurnstileData, TTurnstileOption>) => {
    const { isHidden } = useBehavior(widget);
    const { updateWidgetValue } = useUpdateWidgetValue(widget);
    const { triggerActionsByType } = useTriggerAction(widget);
    const { language } = useApp();
    const isOnline = useOnline();
    const theme = useResolvedTheme() === DefaultTheme.dark ? 'dark' : 'light';

    const containerRef = useRef<HTMLDivElement>(null);
    // Last error code that triggered onFailure; Cloudflare's auto-retries with the same code only refresh data.value.
    const lastReportedErrorRef = useRef<string | undefined>(undefined);
    // A valid token survives a connectivity blip, so going offline only blocks the check while there is no token yet.
    const [hasToken, setHasToken] = useState(false);
    const isOfflineWithoutToken = !isOnline && !hasToken;

    const siteKey = widget.data?.siteKey;
    const appearance = normalizeAppearance(widget.options?.appearance);
    const size = normalizeSize(widget.options?.size);

    // The value is written before the action fires, so ProcessAction carries it under the widget uid.
    const reportSuccess = useEffectEvent((token: string) => {
        lastReportedErrorRef.current = undefined;
        setHasToken(true);
        updateWidgetValue(buildSuccessValue(token));
        triggerActionsByType(ConsumerActionTypeEnums.onSuccess).catch(console.error);
    });

    // Every failure is logged and written to data.value; onFailure fires once per distinct error code until the next token,
    // so Cloudflare's ~8 s auto-retries do not dispatch a Scenario action each time.
    // Without a declared onFailure action triggerActionsByType is a no-op, so the error is only logged.
    const reportFailure = useEffectEvent((errorCode: string, errorMessage?: string) => {
        console.error('[Turnstile]', widget.uid, errorCode, errorMessage ?? '');
        setHasToken(false);
        updateWidgetValue(buildErrorValue(errorCode, errorMessage));
        if (lastReportedErrorRef.current === errorCode) {
            return;
        }
        lastReportedErrorRef.current = errorCode;
        triggerActionsByType(ConsumerActionTypeEnums.onFailure).catch(console.error);
    });

    const clearValue = useEffectEvent(() => {
        lastReportedErrorRef.current = undefined;
        setHasToken(false);
        updateWidgetValue(undefined);
    });

    useEffect(() => {
        if (isHidden) {
            return;
        }
        if (!siteKey) {
            console.error('[Turnstile]', widget.uid, 'data.siteKey is missing, the widget is not rendered');
            return;
        }
        if (isOfflineWithoutToken) {
            reportFailure(TurnstileClientError.NO_NETWORK);
            return;
        }

        let cancelled = false;
        let widgetId: string | undefined;
        let loadFailed = false;

        const failLoad = (reason: string) => {
            if (cancelled || loadFailed) {
                return;
            }
            loadFailed = true;
            reportFailure(TurnstileClientError.LOAD_FAILED, reason);
        };

        // The 15 s load timeout lives in loadTurnstileScript (script_load_timeout lands in .catch below). Once rendered,
        // Cloudflare reports its own challenge timeouts through error-callback, so no extra client-side timer is armed.
        const renderOptions: TTurnstileRenderOptions = {
            sitekey: siteKey,
            theme,
            language: language || 'auto',
            size,
            appearance,
            retry: 'auto',
            'refresh-expired': 'auto',
            callback: (token) => {
                if (!cancelled) {
                    reportSuccess(token);
                }
            },
            'error-callback': (errorCode) => {
                if (!cancelled) {
                    reportFailure(String(errorCode));
                }
                // Handled: Cloudflare keeps retrying on its own (~8 s); a new error code fires onFailure again.
                return true;
            },
            'expired-callback': () => {
                if (!cancelled) {
                    clearValue();
                }
            },
            'unsupported-callback': () => failLoad(TurnstileLoadError.UNSUPPORTED_BROWSER),
        };

        loadTurnstileScript()
            .then((api) => {
                const container = containerRef.current;
                if (cancelled || !container) {
                    return;
                }
                try {
                    widgetId = api.render(container, renderOptions) ?? undefined;
                } catch (error) {
                    failLoad(getErrorMessage(error));
                }
            })
            .catch((error: unknown) => failLoad(getErrorMessage(error)));

        return () => {
            cancelled = true;
            if (widgetId) {
                try {
                    window.turnstile?.remove(widgetId);
                } catch (error) {
                    console.error('[Turnstile]', widget.uid, error);
                }
            }
        };
    }, [isHidden, siteKey, appearance, size, theme, language, isOfflineWithoutToken, widget.uid]);

    if (isHidden || !siteKey) {
        return null;
    }

    return (
        <div data-test={widget.uid} className={styles.wrapper} style={getInlineStyles({ margin: widget.options?.margin })}>
            <div ref={containerRef} className={clsx(styles.container, styles[size], appearance === 'interaction-only' && styles.interactionOnly)} />
        </div>
    );
};
